import { Component, Input, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { saveAs } from 'file-saver';
import { combineLatest, Subscription } from 'rxjs';
import { Material } from 'src/app/classes/material.class';
import { PlanningFile } from 'src/app/classes/planning-file';
import { Realisation } from 'src/app/classes/realisation';
import { CodaltComponent } from 'src/app/codalt.component';
import { ImageModalComponent } from 'src/app/generic/components/image-modal/image-modal.component';
import { mergePlanningMaterialPivots, MaterialService } from 'src/app/services/material/material.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { LocalStorage } from 'src/app/storage.class';
import {
    TracklineOrderFormComponent,
    TracklineOrderFormDialogData,
} from 'src/app/trackline/pages/trackline-order-form/trackline-order-form.component';
import { Utils } from 'src/app/utils.class';

@Component({
    selector: 'app-realisation-detail',
    templateUrl: './realisation-detail.component.html',
    styleUrls: ['./realisation-detail.component.scss'],
    standalone: false,
})
export class RealisationDetailComponent extends CodaltComponent implements OnChanges {
    @Input({ required: true }) realisation: Realisation;

    date: Date;
    dateString: string;
    showWorkWith = false;
    showWorkWithFrom: Date;
    materials: Material[] = [];

    constructor(
        private dialog: MatDialog,
        private materialService: MaterialService,
        private planningService: PlanningService
    ) {
        super();
    }

    ngOnChanges(): void {
        this.date = Utils.setTimeNewDate(this.realisation.bookdate, 0, 0);
        this.dateString = Utils.dateString(this.date);
        this.showWorkWithFrom = new Date(this.date);
        do {
            this.showWorkWithFrom.setDate(this.showWorkWithFrom.getDate() - 1);
        } while (!Utils.isWorkingday(this.showWorkWithFrom));
        this.showWorkWith = this.environment.allowWorksWith || this.showWorkWithFrom.getTime() <= Date.now();

        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
        this.materials = [];
        this.subscriptions.add(
            combineLatest([
                this.materialService.getForPlanning(this.realisation.planning_id),
                this.planningService.materials$,
            ]).subscribe(([pivots, catalog]) => {
                this.materials = mergePlanningMaterialPivots(catalog, pivots);
            })
        );
    }

    openFile(file: PlanningFile) {
        const fileName = file.name?.toLowerCase() ?? '';
        const isPdf = fileName.endsWith('.pdf');
        const isImage = /\.(jpe?g|png|gif|svg)$/.test(fileName);

        if (!isPdf && !isImage) {
            if (file.id) {
                this.downloadFile(file);
            }
            return;
        }

        if (!file.file) {
            return;
        }

        const url = `${this.environment.apiEndpoint}file/planning/${file.file}?access_token=${LocalStorage.getUserToken()}`;

        this.dialog.open(ImageModalComponent, {
            panelClass: 'image-viewer',
            width: '99vw',
            maxWidth: '1300px',
            height: '99vh',
            minHeight: '500px',
            maxHeight: '99vh',
            hasBackdrop: true,
            data: {
                viewIndex: 0,
                ...(isPdf ? { singlePdf: url } : { images: [url] }),
            },
        });
    }

    downloadFile(file: PlanningFile) {
        this.subscriptions.add(
            this.planningService.getFile(file.id).subscribe({
                next: data => saveAs(data, file.name),
                error: error => console.error('Error downloading file', error),
            })
        );
    }

    openOrderForm(planningId: number) {
        this.dialog.open<TracklineOrderFormComponent, TracklineOrderFormDialogData>(TracklineOrderFormComponent, {
            data: { planningId },
            maxWidth: '1050px',
            width: '100%',
            panelClass: 'order-form-dialog',
        });
    }
}
